import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, PenLine, Droplets, MailOpen, MousePointerClick,
  MessageSquareText, CalendarClock, CheckCircle2,
} from 'lucide-react';

const API = '/api';

// Outreach Analytics — the warm-list letters + recruit drip, per person:
// sent -> opened -> clicked -> replied. Letters may lack open tracking; the UI
// shows a dash for unknown, never a fake zero — replies are the honest metric.

const dash = <span className="text-hg-text-dim" title="No tracking data available">—</span>;

function n(v) {
  return v === null || v === undefined ? dash : v.toLocaleString();
}

function Rollup({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-hg-surface border border-hg-border rounded-sm p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">{label}</div>
          <div className="font-display text-2xl text-hg-text tracking-wider mt-2">{value}</div>
          {sub && <div className="text-[11px] text-hg-text-dim font-sans mt-1">{sub}</div>}
        </div>
        <div className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 bg-hg-green/10">
          <Icon size={16} className="text-hg-green" strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
}

function RepliedCell({ replied, replied_at }) {
  if (!replied) return <td className="px-3 py-2.5 text-right text-hg-text-dim">—</td>;
  return (
    <td className="px-3 py-2.5 text-right text-hg-green whitespace-nowrap">
      <CheckCircle2 size={11} className="inline mr-1" />
      {String(replied_at || '').slice(0, 10)}
    </td>
  );
}

export default function OutreachAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/outreach/analytics`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load outreach analytics.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const roll = data?.rollups || {};
  const letters = data?.letters?.rows || [];
  const drip = data?.drip?.rows || [];

  return (
    <div className="space-y-4" data-testid="outreach-analytics">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">
          Warm-List Outreach — Letters &amp; Recruit Drip
        </h3>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-1 text-[11px] font-sans text-hg-text-dim hover:text-hg-green disabled:opacity-40">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>
      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {loading && !data && <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-hg-green" /></div>}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Rollup testid="outreach-letters" icon={PenLine} label="Letters sent"
              value={roll.letters_sent ?? 0}
              sub={`${data.letters?.staged ?? 0} still staged · ${roll.letter_replies ?? 0} replied`} />
            <Rollup testid="outreach-r1" icon={MailOpen} label="Drip R1 open rate"
              value={roll.r1_open_rate === null || roll.r1_open_rate === undefined
                ? '—' : `${Math.round(roll.r1_open_rate * 100)}%`}
              sub={`${roll.r1_opened ?? 0} of ${roll.r1_sent ?? 0} R1 recipients opened`} />
            <Rollup testid="outreach-replies" icon={MessageSquareText} label="Replies"
              value={roll.replies_total ?? 0}
              sub={`${roll.letter_replies ?? 0} letters · ${roll.drip_replies ?? 0} drip`} />
            <Rollup testid="outreach-booking" icon={CalendarClock} label="Booking-link clicks"
              value={roll.booking_clicks ?? 0} sub="Letters + drip combined" />
          </div>

          {/* Personal letters */}
          <div className="bg-hg-surface border border-hg-border rounded-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-hg-border">
              <span className="text-[11px] font-sans text-hg-text uppercase tracking-wider inline-flex items-center gap-1.5">
                <PenLine size={12} className="text-hg-green" /> Personal letters ({letters.length})
              </span>
              <span className="text-[10px] font-sans text-hg-text-dim">
                1:1 sends from randall@ — opens can undercount; replies are the honest metric
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-sans" data-testid="outreach-letters-table">
                <thead>
                  <tr className="text-left text-[10px] text-hg-text-dim uppercase tracking-wider border-b border-hg-border">
                    <th className="px-3 py-2 font-normal">Recipient</th>
                    <th className="px-3 py-2 font-normal hidden md:table-cell">Subject</th>
                    <th className="px-3 py-2 font-normal">Sent</th>
                    <th className="px-3 py-2 font-normal text-right">Opens</th>
                    <th className="px-3 py-2 font-normal text-right hidden sm:table-cell">Clicks</th>
                    <th className="px-3 py-2 font-normal text-right">Replied</th>
                  </tr>
                </thead>
                <tbody>
                  {letters.map((l) => (
                    <tr key={`${l.email}-${l.campaign_id || l.subject}`}
                        className={`border-b border-hg-border/40 ${l.status !== 'sent' ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2.5 text-hg-text whitespace-nowrap">{l.name}
                        <span className="text-hg-text-dim hidden lg:inline"> · {l.email}</span></td>
                      <td className="px-3 py-2.5 text-hg-text-dim max-w-[220px] truncate hidden md:table-cell" title={l.subject}>{l.subject}</td>
                      <td className="px-3 py-2.5 text-hg-text-dim whitespace-nowrap">
                        {l.status === 'sent' ? String(l.sent_at || '').slice(0, 10)
                          : <span className="text-amber-300 uppercase text-[10px] tracking-wider">staged</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right text-hg-text">{l.status === 'sent' ? n(l.opens) : ''}</td>
                      <td className="px-3 py-2.5 text-right text-hg-text hidden sm:table-cell">
                        {l.status === 'sent' ? n(l.clicks) : ''}
                        {l.booking_click && <CalendarClock size={10} className="inline ml-1 text-hg-green" title="Clicked the booking link" />}
                      </td>
                      <RepliedCell replied={l.replied} replied_at={l.replied_at} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recruit drip */}
          <div className="bg-hg-surface border border-hg-border rounded-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-hg-border">
              <span className="text-[11px] font-sans text-hg-text uppercase tracking-wider inline-flex items-center gap-1.5">
                <Droplets size={12} className="text-hg-green" /> Recruit drip ({drip.length} enrolled)
              </span>
              <span className="text-[10px] font-sans text-hg-text-dim">Engagement from the hourly MailerLite sync</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-sans" data-testid="outreach-drip-table">
                <thead>
                  <tr className="text-left text-[10px] text-hg-text-dim uppercase tracking-wider border-b border-hg-border">
                    <th className="px-3 py-2 font-normal">Lead</th>
                    <th className="px-3 py-2 font-normal hidden sm:table-cell">Tier</th>
                    <th className="px-3 py-2 font-normal">Steps</th>
                    <th className="px-3 py-2 font-normal text-right">Opened</th>
                    <th className="px-3 py-2 font-normal text-right hidden sm:table-cell">Clicked</th>
                    <th className="px-3 py-2 font-normal text-right">Replied</th>
                  </tr>
                </thead>
                <tbody>
                  {drip.map((d) => (
                    <tr key={d.email} className="border-b border-hg-border/40">
                      <td className="px-3 py-2.5 text-hg-text whitespace-nowrap">{d.name}
                        <span className="text-hg-text-dim hidden lg:inline"> · {d.email}</span></td>
                      <td className="px-3 py-2.5 hidden sm:table-cell">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                          d.tier === 'full' ? 'border-hg-green/40 text-hg-green' : 'border-hg-border text-hg-text-dim'}`}>
                          {d.tier === 'full' ? 'full' : 'pulse'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-hg-text-dim whitespace-nowrap">
                        {(d.steps || []).map((s) => s.tag).filter(Boolean).join(' → ') || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {!d.engagement_synced ? dash
                          : d.opened
                            ? <span className="text-hg-green"><MailOpen size={11} className="inline mr-1" />{d.open_count || ''}</span>
                            : <span className="text-hg-text-dim">no</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right hidden sm:table-cell">
                        {!d.engagement_synced ? dash
                          : d.clicked
                            ? <span className="text-hg-green">
                                <MousePointerClick size={11} className="inline mr-1" />yes
                                {d.clicked_booking && <CalendarClock size={10} className="inline ml-1" title="Clicked the booking link" />}
                              </span>
                            : <span className="text-hg-text-dim">no</span>}
                      </td>
                      <RepliedCell replied={d.replied} replied_at={d.replied_at} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
